"use client";

import React from "react";
import { Grid, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import moment from "moment-timezone";

const TimeSlotCard = ({
  timeSlot,
  theme,
  selectedTime,
  isToday,
  handleTimeSelect,
  userTimezone,
  selectedDay,
  columnSize = 6,
  cardSx = {},
  textSx = {},
}) => {
  const slotTime = React.useMemo(() => {
    let momentObj;

    // Use fullTime if available (preserves timezone)
    if (timeSlot?.fullTime && moment.isMoment(timeSlot.fullTime)) {
      momentObj = timeSlot.fullTime.clone();
    }
    // Use start if it's a moment object
    else if (moment.isMoment(timeSlot?.start)) {
      momentObj = timeSlot.start.clone();
    }
    // Use the moment object directly if timeSlot is a moment
    else if (moment.isMoment(timeSlot)) {
      momentObj = timeSlot.clone();
    }
    // Reconstruct from time string in user timezone
    else if (timeSlot?.time && selectedDay) {
      const selectedDate = moment(selectedDay, "DD-MM-YYYY");
      momentObj = moment.tz(
        `${selectedDate.format("YYYY-MM-DD")} ${timeSlot.time}`,
        "YYYY-MM-DD HH:mm",
        userTimezone,
      );
    }
    // Fallback: Create in user timezone
    else {
      momentObj = moment.tz(timeSlot, userTimezone);
    }

    // Ensure it's in the correct timezone
    return momentObj ? momentObj.tz(userTimezone) : moment.tz(userTimezone);
  }, [timeSlot, selectedDay, userTimezone]);

  const now = moment.tz(userTimezone);
  const isPastTime = isToday && slotTime.isBefore(now);
  const isSelected = selectedTime && slotTime.isSame(selectedTime);
  const isBlocked = timeSlot?.isBlocked || false;
  const tinted = Boolean(theme.palette.tintedSurface);
  const activeBorderColor = tinted
    ? theme.palette.text.primary
    : theme.palette.mode === "light"
    ? theme.palette.background.accent
    : theme.palette.primary.main;
  const idleBorderColor = tinted
    ? alpha(theme.palette.text.primary, 0.25)
    : theme.palette.grey[300];

  return (
    <Grid size={columnSize} key={slotTime.format("HH:mm")}>
      <Grid
        sx={{
          textAlign: "center",
          cursor: isPastTime || isBlocked ? "default" : "pointer",
          borderRadius: "5px",
          borderStyle: "solid",
          borderColor: isSelected
            ? activeBorderColor
            : isBlocked
            ? theme.palette.error.light
            : idleBorderColor,
          borderWidth: isSelected ? "2px" : "1px",
          height: 40,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: isPastTime || isBlocked ? 0.5 : 1,
          backgroundColor: isBlocked
            ? theme.palette.error.lighter
            : tinted
            ? theme.palette.background.paper
            : "transparent",
          "&:hover": {
            borderColor:
              isPastTime || isBlocked
                ? isBlocked
                  ? theme.palette.error.light
                  : idleBorderColor
                : activeBorderColor,
            borderWidth: isSelected ? "2px" : "1px",
          },
          ...cardSx,
        }}
        onClick={() => !isPastTime && !isBlocked && handleTimeSelect(slotTime)}
      >
        <Typography
          variant="body3"
          sx={{
            fontWeight: 500,
            textDecoration: isBlocked ? "line-through" : "none",
            ...textSx,
          }}
        >
          {slotTime.format("hh:mm A")}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default TimeSlotCard;
